import { cn } from '@/lib/utils';
import {
  AlertTriangle, Clock, ShieldAlert, ShieldCheck, PackageCheck,
  TrendingDown, Globe, Users, DollarSign, Activity, MessageSquare,
  Timer, RotateCcw, Boxes, FileText, Tag
} from 'lucide-react';

const SIGNAL_CARDS = [
  // Row 1
  { abbr: 'ODR', value: '0.8%', icon: AlertTriangle, status: 'warning', label: 'Order Defect Rate' },
  { abbr: 'LSR', value: '2.1%', icon: Clock, status: 'safe', label: 'Late Shipment Rate' },
  { abbr: 'POL', value: '3', icon: ShieldAlert, status: 'critical', label: 'Policy Warnings' },
  { abbr: 'A-Z', value: '0.2%', icon: ShieldCheck, status: 'safe', label: 'A-to-Z Claim Rate' },
  { abbr: 'VTR', value: '98%', icon: PackageCheck, status: 'safe', label: 'Valid Tracking Rate' },
  { abbr: 'CR', value: '1.8%', icon: TrendingDown, status: 'warning', label: 'Cancellation Rate' },
  { abbr: 'GEO', value: 'Normal', icon: Globe, status: 'safe', label: 'Login Geo Drift' },
  { abbr: 'PER', value: '0', icon: Users, status: 'safe', label: 'Permission Changes' },
  // Row 2
  { abbr: 'BPC', value: 'None', icon: DollarSign, status: 'safe', label: 'Bulk Price Changes' },
  { abbr: 'API', value: 'Normal', icon: Activity, status: 'safe', label: 'API Activity Spike' },
  { abbr: 'NFR', value: '2.4%', icon: MessageSquare, status: 'warning', label: 'Negative Feedback Rate' },
  { abbr: 'MRT', value: '4h', icon: Timer, status: 'safe', label: 'Message Response Time' },
  { abbr: 'RDR', value: '0.3%', icon: RotateCcw, status: 'safe', label: 'Return Dissatisfaction' },
  { abbr: 'ISR', value: '5 ASINs', icon: Boxes, status: 'critical', label: 'ASIN Changes' },
  { abbr: 'ASN', value: '0', icon: FileText, status: 'safe', label: 'Duplicate ASINs' },
  { abbr: 'BRD', value: 'None', icon: Tag, status: 'safe', label: 'Brand Name Changes' },
];

const TICKER_ITEMS = [
  'Stranded Inventory', 'Carrier Scan Delay', 'Delivery Promise Breach',
  'Buy Box Percentage', 'Session Rate', 'Conversion Rate',
  'IPI Score', 'Weekend Shipment Failure', 'Shipping Cost Spike',
  'Negative Review Velocity', 'Refund Processing Time', 'Category Change Frequency'
];

function getStatusStyles(status) {
  if (status === 'critical') return {
    dot: 'bg-[hsl(0,84%,60%)]',
    border: 'border-[hsl(0,84%,60%)/0.5]',
    bg: 'bg-[hsl(0,84%,60%)/0.06]',
    text: 'text-[hsl(0,84%,60%)]',
  };
  if (status === 'warning') return {
    dot: 'bg-[hsl(45,93%,58%)]',
    border: 'border-[hsl(45,93%,58%)/0.5]',
    bg: 'bg-[hsl(45,93%,58%)/0.06]',
    text: 'text-[hsl(45,93%,58%)]',
  };
  return {
    dot: 'bg-[hsl(142,72%,45%)]',
    border: 'border-[hsl(142,72%,45%)/0.4]',
    bg: 'bg-[hsl(142,72%,45%)/0.05]',
    text: 'text-[hsl(142,72%,45%)]',
  };
}

function SignalCard({ signal }) {
  const s = getStatusStyles(signal.status);
  const Icon = signal.icon;
  return (
    <div className={cn(
      'w-[100px] h-[90px] rounded-lg border p-2.5 flex-shrink-0 relative',
      s.border, s.bg
    )}>
      {/* Status dot */}
      <div className={cn('absolute top-2 right-2 w-2 h-2 rounded-full', s.dot)} />
      {/* Icon */}
      <Icon className={cn('w-4 h-4 mb-1.5 opacity-80', s.text)} strokeWidth={1.5} />
      {/* Abbreviation */}
      <p className="text-[10px] text-muted-foreground leading-none mb-1">{signal.abbr}</p>
      {/* Value */}
      <p className={cn('text-sm font-bold leading-tight tabular-nums', s.text)}>{signal.value}</p>
    </div>
  );
}

export function ScrollingSignalCards() {
  const row1 = SIGNAL_CARDS.slice(0, 8);
  const row2 = SIGNAL_CARDS.slice(8, 16);

  return (
    <div className="w-full overflow-hidden space-y-3">
      {/* Row 1 - scrolls left */}
      <div className="relative">
        <div className="flex gap-2.5 animate-scroll-left">
          {[...row1, ...row1, ...row1].map((signal, i) => (
            <SignalCard key={`r1-${i}`} signal={signal} />
          ))}
        </div>
      </div>
      {/* Row 2 - scrolls right */}
      <div className="relative">
        <div className="flex gap-2.5 animate-scroll-right">
          {[...row2, ...row2, ...row2].map((signal, i) => (
            <SignalCard key={`r2-${i}`} signal={signal} />
          ))}
        </div>
      </div>

      {/* Bottom ticker */}
      <div className="mt-6 overflow-hidden">
        <div className="flex gap-6 animate-scroll-ticker">
          {[...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(188,100%,55%)] opacity-60" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* Plus text */}
      <p className="text-center text-xs text-muted-foreground mt-3 opacity-60">
        Plus: IPI Score · Stranded Inventory · Carrier Scan Delay · Delivery Promise Breach · and 30+ more
      </p>
    </div>
  );
}
